// File: frontend/src/components/marketing/FeatureShowcase.js
//
// Features (#features): six things an operator runs from PesaFii, each with its
// own illustration.
//
// ── DESKTOP: A READING LIST BESIDE ONE STICKY ILLUSTRATION ──────────────────
//
// The list is on the left and a single illustration is sticky on the right. As
// each item passes the reading line it becomes the active one and the
// illustration changes; choosing an item does the same and brings it to that
// line, so scroll position and selection agree afterwards.
//
// Only the active item's explanation is expanded. Each item keeps a FIXED
// height regardless, because a collapsing item would change the height of
// everything below it and leave every ScrollTrigger measured against a layout
// that no longer exists. Fixing the height means the page never moves and no
// refresh is needed.
//
// The collapse only happens under `html.js`: with JavaScript off, every
// explanation is open, which is what the prerendered page and crawlers get.
//
// ── PHONES: A COMPACT CHAPTER PICKER, NOT A SECOND SCROLL STORY ─────────────
//
// Six chapters each carrying their own illustration inline measured 4.35
// viewports on a 390x844 phone — by a wide margin the longest thing on the
// page. Turning it into a sticky story instead would have added roughly three
// more. So phones get one active chapter — heading, explanation and its
// visual — with a chip to change chapter and the same progress readout. The
// whole section then costs about one viewport, and every chapter is still one
// tap away. This is the "readable alternative" the sticky presentation falls
// back to, and the static page still contains all six descriptions.

import React, { useEffect, useRef, useState } from "react";
import { Box, ButtonBase, Container, Stack, Typography, alpha } from "@mui/material";
import RouterIcon from "@mui/icons-material/Router";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import BrushOutlinedIcon from "@mui/icons-material/BrushOutlined";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import GroupsIcon from "@mui/icons-material/Groups";
import {
  LocationsVisual, VouchersVisual, WithdrawVisual, PortalVisual, ReportsVisual, TeamVisual,
} from "./visuals/FeatureVisuals";
import { gsap, useGSAP, ScrollTrigger, MQ, HEADER_H, prefersReducedMotion, useReveal } from "./landingMotion";

const FEATURES = [
  { key: "routers", icon: RouterIcon, title: "Routers & locations", desc: "See which routers are online, how long they have been up and who is connected, across every location.", Visual: LocationsVisual },
  { key: "vouchers", icon: ConfirmationNumberIcon, title: "Packages & vouchers", desc: "Set time and data packages at your own prices, then print or share voucher codes for customers to redeem.", Visual: VouchersVisual },
  { key: "money", icon: AccountBalanceWalletIcon, title: "Mobile Money & withdrawals", desc: "Customers pay with MTN Mobile Money or Airtel Money. You withdraw your earnings to Mobile Money, protected by your PIN.", Visual: WithdrawVisual },
  { key: "portal", icon: BrushOutlinedIcon, title: "Your branded login page", desc: "Customise the page customers see when they join your Wi-Fi: your photo, name, colours and support number.", Visual: PortalVisual },
  { key: "reports", icon: QueryStatsIcon, title: "Sales & reports", desc: "Follow sales by package, busy hours and data use, for the whole business or one router.", Visual: ReportsVisual },
  { key: "team", icon: GroupsIcon, title: "Team & agents", desc: "Give staff and voucher agents their own logins, with access limited to what their role needs.", Visual: TeamVisual },
];

// Also used as a React style prop, which React freezes: GSAP always gets a
// copy ({ ...HIDDEN }), because gsap.set writes into the object it is given.
const HIDDEN = { visibility: "hidden", opacity: 0 };

// How long scroll updates are ignored after a click. Choosing chapter 6 from
// chapter 1 scrolls past four other items, and without this every one of them
// would light up on the way past.
const CLICK_LOCK_MS = 1000;

function Progress({ index, total, sx }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={sx}>
      <Typography
        aria-hidden="true"
        sx={{ fontSize: 12.5, fontWeight: 700, color: "text.secondary", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}
      >
        {index + 1} / {total}
      </Typography>
      <Box sx={(t) => ({ flex: 1, height: 2, borderRadius: 99, bgcolor: alpha(t.palette.text.primary, 0.12), overflow: "hidden" })}>
        <Box sx={{
          height: "100%", bgcolor: "primary.main", borderRadius: 99,
          width: `${((index + 1) / total) * 100}%`, transition: "width 0.35s ease",
        }} />
      </Box>
    </Stack>
  );
}

export default function FeatureShowcase() {
  const [active, setActive] = useState(0);
  const root = useReveal();
  const panel = useRef(null);
  const shown = useRef(0);
  const lockUntil = useRef(0);

  const { contextSafe } = useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add({ desktop: MQ.isDesktop, always: MQ.always }, (ctx) => {
        if (!ctx.conditions.desktop) return undefined;
        gsap.utils.toArray("[data-feature]", root.current).forEach((item, i) => {
          ScrollTrigger.create({
            trigger: item,
            start: "top 58%",
            end: "bottom 58%",
            onToggle: (self) => {
              if (!self.isActive) return;
              if (Date.now() < lockUntil.current) return;
              setActive(i);
            },
          });
        });
        return undefined;
      });
    },
    { scope: root, dependencies: [] }
  );

  const swap = contextSafe((from, to) => {
    const q = gsap.utils.selector(panel);
    const layer = (i) => q(`[data-feature-visual="${i}"]`)[0];
    const all = FEATURES.map((_, i) => layer(i));
    gsap.killTweensOf(all);
    gsap.set(all.filter((_, i) => i !== from && i !== to), { ...HIDDEN });
    if (prefersReducedMotion()) {
      gsap.set(layer(from), { ...HIDDEN });
      gsap.set(layer(to), { visibility: "visible", opacity: 1, y: 0, scale: 1 });
      return;
    }
    const dir = to > from ? 1 : -1;
    gsap.to(layer(from), { autoAlpha: 0, y: -20 * dir, scale: 0.98, duration: 0.3, ease: "power2.in" });
    gsap.fromTo(layer(to), { autoAlpha: 0, y: 28 * dir, scale: 0.98 }, { autoAlpha: 1, y: 0, scale: 1, duration: 0.55, delay: 0.12, ease: "power3.out" });
  });

  useEffect(() => {
    if (shown.current === active) return;
    swap(shown.current, active);
    shown.current = active;
  }, [active, swap]);

  const choose = (i) => {
    setActive(i);
    // On desktop, bring the chosen item to the reading line so scrolling
    // continues from there — and ignore the triggers it passes on the way.
    const item = root.current?.querySelectorAll("[data-feature]")[i];
    if (item && window.matchMedia?.(MQ.isDesktop).matches) {
      lockUntil.current = Date.now() + CLICK_LOCK_MS;
      const top = item.getBoundingClientRect().top + window.scrollY - window.innerHeight * 0.58 + item.offsetHeight / 2;
      window.scrollTo({ top, behavior: prefersReducedMotion() ? "auto" : "smooth" });
    }
  };

  const ActiveVisual = FEATURES[active].Visual;

  return (
    <Box component="section" id="features" ref={root} aria-labelledby="features-title"
      sx={{ py: { xs: 7, md: 10 }, scrollMarginTop: HEADER_H, position: "relative" }}>
      <Container maxWidth="lg" sx={{ minWidth: 0 }}>
        <Box sx={{ maxWidth: 720, mb: { xs: 3, md: 5 } }}>
          <Typography data-reveal="" variant="overline" component="p" sx={{ color: "primary.main", mb: 1 }}>Features</Typography>
          <Typography data-reveal="" id="features-title" variant="h1" component="h2" sx={{ color: "text.primary" }}>
            Your routers, sales and team in one place
          </Typography>
          <Typography data-reveal="" variant="body1" sx={{ mt: 1.5, color: "text.secondary" }}>
            Six things you run from PesaFii every day.
          </Typography>
        </Box>

        {/* ── Phones: chapter chips, one open chapter ─────────────────────── */}
        <Box sx={{ display: { xs: "block", md: "none" } }}>
          <Box
            sx={{
              display: "flex", gap: 1, overflowX: "auto", pb: 1.5, mx: -2, px: 2,
              scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {FEATURES.map((f, i) => (
              <ButtonBase
                key={f.key}
                onClick={() => setActive(i)}
                aria-pressed={i === active}
                sx={(t) => ({
                  flex: "0 0 auto", px: 1.75, py: 1, borderRadius: 99, fontSize: 13, fontWeight: 700,
                  border: "1px solid",
                  borderColor: i === active ? "primary.main" : "divider",
                  color: i === active ? "primary.main" : "text.secondary",
                  bgcolor: i === active ? alpha(t.palette.primary.main, 0.1) : "transparent",
                  "&.Mui-focusVisible": { outline: `2px solid ${t.palette.primary.main}`, outlineOffset: 2 },
                })}
              >
                {f.title}
              </ButtonBase>
            ))}
          </Box>

          <Progress index={active} total={FEATURES.length} sx={{ mb: 2.5, mt: 0.5 }} />

          <Typography component="h3" variant="h3" sx={{ color: "text.primary", mb: 1 }}>
            {FEATURES[active].title}
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary", mb: 2.5 }}>
            {FEATURES[active].desc}
          </Typography>
          <Box aria-hidden><ActiveVisual /></Box>

          {/* The static page carries every description, so a crawler and a
              JavaScript-off reader still get all six. */}
          <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, "html.js &": { display: "none" } }}>
            {FEATURES.filter((_, i) => i !== active).map((f) => (
              <Box component="li" key={f.key} sx={{ mt: 3 }}>
                <Typography component="h3" variant="h3" sx={{ color: "text.primary", mb: 1 }}>{f.title}</Typography>
                <Typography variant="body1" sx={{ color: "text.secondary" }}>{f.desc}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── Desktop: reading list + sticky illustration ─────────────────── */}
        <Box sx={{ display: { xs: "none", md: "grid" }, gridTemplateColumns: "0.85fr 1.15fr", gap: 7, alignItems: "start" }}>
          <Box>
            <Progress index={active} total={FEATURES.length} sx={{ mb: 2, maxWidth: 260 }} />
            <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0, minWidth: 0 }}>
              {FEATURES.map((f, i) => {
                const Icon = f.icon;
                const on = i === active;
                return (
                  <Box
                    component="li"
                    key={f.key}
                    data-feature=""
                    data-active={on || undefined}
                    sx={{
                      position: "relative",
                      // Fixed height: see the header note on why this must not
                      // depend on which explanation is open.
                      minHeight: "28vh",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ width: "100%", pl: 3, position: "relative" }}>
                      {/* Active marker */}
                      <Box aria-hidden sx={(t) => ({
                        position: "absolute", left: 0, top: 4, bottom: 4, width: 3, borderRadius: 99,
                        bgcolor: on ? "primary.main" : alpha(t.palette.text.primary, 0.12),
                        transform: on ? "scaleY(1)" : "scaleY(0.5)", transformOrigin: "center",
                        transition: "transform 0.35s ease, background-color 0.35s ease",
                      })} />
                      <Typography component="h3" variant="h3" sx={{ m: 0, color: on ? "text.primary" : "text.secondary", transition: "color 0.35s ease" }}>
                        <ButtonBase
                          onClick={() => choose(i)}
                          aria-pressed={on}
                          sx={{ font: "inherit", color: "inherit", display: "flex", alignItems: "center", gap: 1.5, textAlign: "left", borderRadius: 2, py: 0.5, pr: 1,
                            "&.Mui-focusVisible": { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: 2 } }}
                        >
                          <Box component="span" sx={(t) => ({
                            width: 40, height: 40, borderRadius: 2.5, flexShrink: 0, display: "grid", placeItems: "center",
                            bgcolor: alpha(t.palette.primary.main, on ? 0.16 : 0.08),
                            color: on ? "primary.main" : alpha(t.palette.primary.main, 0.55),
                            transition: "background-color 0.35s ease, color 0.35s ease",
                          })}>
                            <Icon sx={{ fontSize: 21 }} />
                          </Box>
                          <Box component="span">{f.title}</Box>
                        </ButtonBase>
                      </Typography>
                      {/* Only the active explanation is open. The 0fr → 1fr grid
                          collapse animates height without measuring anything,
                          and leaves the text in the DOM for screen readers and
                          for the static page. */}
                      <Box
                        sx={{
                          display: "grid", gridTemplateRows: "1fr", transition: "grid-template-rows 0.35s ease, opacity 0.35s ease",
                          "html.js li:not([data-active]) > * > &": { gridTemplateRows: "0fr", opacity: 0 },
                        }}
                      >
                        <Box sx={{ overflow: "hidden", minHeight: 0 }}>
                          <Typography variant="body1" sx={{ pt: 1, color: "text.secondary", maxWidth: 460 }}>{f.desc}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* One sticky illustration.
              Anchored just under the navigation rather than centred on the
              viewport: centring it (top: calc(50vh - 230px)) only fits while
              the panel is short, and the photographic scenes made it tall
              enough to hang off the bottom of a 768px laptop. Under the header
              the panel has the whole viewport below it, and the scene caps in
              visuals/FeatureVisuals keep it inside that. */}
          <Box sx={{ position: "sticky", top: `${HEADER_H + 24}px`, minWidth: 0 }}>
            <Box ref={panel} aria-hidden sx={(t) => ({
              position: "relative", display: "grid", alignItems: "center", minHeight: 420,
              p: 3, borderRadius: 5, border: "1px solid", borderColor: "divider",
              background: `radial-gradient(420px 260px at 70% 20%, ${alpha(t.palette.primary.main, t.palette.mode === "dark" ? 0.12 : 0.08)}, transparent 70%), ${alpha(t.palette.text.primary, 0.025)}`,
              "& > *": { gridArea: "1 / 1" },
            })}>
              {FEATURES.map((f, i) => {
                const Visual = f.Visual;
                return (
                  <Box key={f.key} data-feature-visual={i} style={i === 0 ? undefined : HIDDEN} sx={{ minWidth: 0 }}>
                    <Visual />
                  </Box>
                );
              })}
            </Box>
            <Typography sx={{ mt: 1.25, textAlign: "center", fontSize: 11.5, color: "text.disabled" }}>
              Illustrative sample data
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
